import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import EntityMessageManager from './EntityMessageManager.js';
import EntityBrowser from './EntityBrowser.js'
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import EntityInfo from './EntityInfo';
import SelectedMessage from './SelectedMessage';
import { LogViewer } from './LogViewer';
import { isEntityInfoValid } from '../modules/isEntityInfoValid';
import AuthenticationInfo from './AuthenticationInfo';
import Footer from '../components/Footer';
import { hasLicence } from '../modules/subscriptionManagement/hasLicence';
import { useMsal } from "@azure/msal-react";
import jwt_decode from "jwt-decode";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Tooltip } from '@mui/material';

const drawerWidth = "17vw";
const drawerMinWidth = "300px";
const drawerMaxWidth = "400px";

// Create a theme with smaller font sizes and reduced line heights
const smallFontTheme = createTheme({
  typography: {
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 700,   // Increased from 500
    fontWeightBold: 800,
    fontSize: 12, // Base font size (default is 14)
    h1: { fontSize: '1.8rem', lineHeight: 1.1 },
    h2: { fontSize: '1.6rem', lineHeight: 1.1 },
    h3: { fontSize: '1.4rem', lineHeight: 1.15 },
    h4: { fontSize: '1.2rem', lineHeight: 1.15 },
    h5: { fontSize: '1.1rem', lineHeight: 1.2 },
    h6: { fontSize: '1rem', lineHeight: 1.2 },
    subtitle1: { fontSize: '0.875rem', lineHeight: 1.2 },
    subtitle2: { fontSize: '0.8125rem', lineHeight: 1.2 },
    body1: { fontSize: '0.875rem', lineHeight: 1.25 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.25 },
    button: { fontSize: '0.8125rem', lineHeight: 1.2 },
    caption: { fontSize: '0.75rem', lineHeight: 1.2 },
    overline: { fontSize: '0.75rem', lineHeight: 1.2 },
  },
});


const Root = styled('div')({
  display: 'flex',
});

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  minWidth: drawerMinWidth,
  maxWidth: drawerMaxWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    minWidth: drawerMinWidth,
    maxWidth: drawerMaxWidth,
  },
}));

// Left side drawer opener - shows when drawer is hidden where users see a link to open the drawer
const DrawerOpener = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
}));

const ToolbarSpacer = styled('div')(({ theme }) => theme.mixins.toolbar);

const Content = styled('main')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));


export default function App({ setErrorMessage, loggedInAccount }) {
  const [isLicenced, setIsLicenced] = useState("unknown");
  const [logs, setLogs] = useState([]);
  const [selectedEntityAdditionalProperties, setSelectedEntityAdditionalProperties] = useState({});
  const [selectedEntity, setSelectedEntity] = useState();
  const [selectedMessage, setSelectedMessage] = useState();
  const [drawerOpened, setDrawerOpened] = useState(true);
  const [entityInfo, setEntityInfo] = useState({
    namespaceUrl: "",
    entityName: "",
    authenticateVia: "connectionString",
    entityType: "queue",
    connectionString: "",
    sasToken: "",
    sasKey: "",
    sasKeyName: "",
    aadCleintId: "",
    aadCleintSecret: "",
    aadTenantId: "",
    jwtToken: ""
  });

  const { instance } = useMsal();
  const { browserCredentials } = instance;
  const isLoggedIn = !!loggedInAccount;
  useEffect(() => {
    const doAsync = async () => {
      if (loggedInAccount) {
        const token = loggedInAccount.accessToken;
        const jwt = jwt_decode(token);
        setEntityInfo((old) => {
          const entityInfo = {
            ...old,
            authenticateVia: "yourIdentity",
            aadTenantId: jwt["tid"],
          }
          return Object.assign({}, entityInfo, {
            isValid: isEntityInfoValid(entityInfo)
          });
        });
        /////////////////////// Detecting if a user have liecense or not
        try {
          const hasLic = await hasLicence(jwt["tid"], token);
          setIsLicenced(hasLic)
          if (!hasLic) {
            window.appInsights.trackEvent({
              name: "Unlicensed user logged in",
              properties: jwt
            });
            window.dataLayer && window.dataLayer.push({
              event: 'unpaid_user_logged_in',
              unpaid_user_id : jwt["preferred_username"],
              unpaid_user_name: jwt["name"]
            });
          }
        } catch (error) {
          window.appInsights.trackException({
            exception: error,
            properties: {
              process: "Detecting if a user have liecense or not"
            }
          });
        }
      }

    }
    doAsync();
  }, [browserCredentials, loggedInAccount]);


  const onEntityAdditionalInfoProvided = (properties) => {   
    setSelectedEntityAdditionalProperties(old => {
      return {
        ...old,
        ...properties
      }
    })
  }
  
  const onEntitySelected = (entity) => {
    if (entity) {
      const newEntityInfo = {
        ...entityInfo,
        namespaceUrl: entity.namespaceUrl.replace(':443', ''),
        entityName: entity.type === "subscription" 
          ? `${entity.topic.name.replace("~", "/")}/${entity.name}` 
          : entity.name.replace("~", "/"),
        entityType: entity.type,
        isValid: isEntityInfoValid(entityInfo)
      };
      setSelectedEntityAdditionalProperties({}); // clears additional properties in prep for presenting new ones
      setEntityInfo(Object.assign({}, newEntityInfo, {
        isValid: isEntityInfoValid(newEntityInfo)
      }));
      setSelectedEntity(entity);
    }
  }

  const onEntityInfoChanged = (entityInfo) => {
    setEntityInfo({
      ...entityInfo,
      isValid: isEntityInfoValid(entityInfo)
    });
  }

  const onMessageEventReceived = (event) => {
    setLogs((old) => {
      return [...old, event];
    })
  }

  const onLogsCleared = () => {
    setLogs([]);
  }

  return (
    <Root>
      <DrawerOpener style={{display: drawerOpened ? 'none': 'block'}}>
        <div>
        <ToolbarSpacer  />
          <Tooltip title="Open Explorer">
            <IconButton
              aria-label="Open Explorer"
              onClick={(event) => {
                setDrawerOpened(true);
                event.stopPropagation();
              }}
              edge="start"
            >
              <MenuOpenIcon color='primary' sx={{ transform: 'scaleX(-1)' }} />
            </IconButton>
          </Tooltip>
        </div>
      </DrawerOpener>
      <StyledDrawer style={{ display: drawerOpened ? 'block' : 'none' }} variant="permanent" anchor="left">
        <ToolbarSpacer  />
        <Divider />
        <div style={{ paddingTop: "13px", position: "absolute", width: "100%", textAlign: "right" }}>
          <ToolbarSpacer  />
          <Tooltip title="Close Explorer">
            <IconButton 
            aria-label="Close Explorer"
            onClick={() => setDrawerOpened(false)}>
                <MenuOpenIcon color='primary' />
            </IconButton>
          </Tooltip>
        </div>
        {isLoggedIn === true ? <>
          <ThemeProvider theme={smallFontTheme}>
            <EntityBrowser isLoggedIn={isLoggedIn} isLicenced={isLicenced} credentials={browserCredentials} setErrorMessage={setErrorMessage} onEntitySelected={onEntitySelected} onEntityAdditionalInfoProvided={onEntityAdditionalInfoProvided} selectedEntity={selectedEntity} />
          </ThemeProvider>
        </> : <></>
        }
      </StyledDrawer>
      <Content>
        <ToolbarSpacer  />
        <Grid container spacing={1} justifyContent="space-between">
          {isLoggedIn === true && (typeof isLicenced === "boolean" && !isLicenced) ?
            <Grid size={{ xs:12 }}><Alert severity="warning">You're using a free <a href='https://cloudbricks.io/pages/service-bus-cloud-explorer-pricing/' target='_blank' rel="noreferrer">limited version</a> of this software. For full functionality <a href='https://azuremarketplace.microsoft.com/en-us/marketplace/apps/cloudbricks1626490617482.service_bus_sas_1?tab=Overview' target='_blank' rel="noreferrer">create a subscription</a> for your organization.</Alert></Grid>
            : <></>}
          
          <Grid size={{ xs:12, md:8, lg:8, xl:8 }}>
            <StyledPaper >
              <EntityMessageManager onMessageSelected={setSelectedMessage} isLoggedIn={isLoggedIn} isLicenced={isLicenced} setErrorMessage={setErrorMessage} credentials={browserCredentials} entityInfo={entityInfo} onMessageEventReceived={onMessageEventReceived} selectedEntity={selectedEntity} />
            </StyledPaper>
          </Grid>

          <Grid size={{ xs:12, md:4, lg:4, xl:4 }} >
            
            {selectedMessage ? <StyledPaper>
              <SelectedMessage selectedMessage={selectedMessage} />
            </StyledPaper>
            : <></>}
            
            <StyledPaper>
              <EntityInfo isLoggedIn={isLoggedIn} selectedEntityAdditionalProperties={selectedEntityAdditionalProperties} isLicenced={isLicenced} credentials={browserCredentials} selectedEntity={selectedEntity} entityInfo={entityInfo} onEntityInfoChanged={onEntityInfoChanged} />
            </StyledPaper>

            <StyledPaper>
              <AuthenticationInfo isLoggedIn={isLoggedIn} isLicenced={isLicenced} credentials={browserCredentials} entityInfo={entityInfo} onEntityInfoChanged={onEntityInfoChanged} />
            </StyledPaper>

          </Grid>

          <Grid size={{ xs:12, md:12, lg:12, xl:12 }} style={{ paddingTop: "0px" }}>
            <StyledPaper>
              <LogViewer logs={logs} onLogsCleared={onLogsCleared} />
            </StyledPaper>
          </Grid>
        </Grid>
        <Footer />
      </Content>
    </Root>
  );
};